import axios from "axios";
import { config } from "@/config";

const REDIRECT_PARAM_KEY = "redirectUrl";

const LOOPBACK_HOSTNAMES = new Set(["localhost", "127.0.0.1", "[::1]", "::1"]);

const isPrivateNetworkIP = (hostname: string): boolean => {
  // IPv4 private ranges (RFC 1918) + link-local
  const ipv4Match = hostname.match(/^(\d+)\.(\d+)\.(\d+)\.(\d+)$/);
  if (ipv4Match) {
    const [, a, b] = ipv4Match.map(Number);
    return (
      a === 10 ||                            // 10.0.0.0/8
      (a === 172 && b >= 16 && b <= 31) ||   // 172.16.0.0/12
      (a === 192 && b === 168) ||            // 192.168.0.0/16
      (a === 169 && b === 254)               // 169.254.0.0/16 (link-local)
    );
  }

  // IPv6 unique local (fc00::/7) and link-local (fe80::/10)
  // Only match bracketed IPv6 literals like [fe80::1], not domains like fd.example.com
  const ipv6Match = hostname.match(/^\[([0-9a-f:]+)\]$/i);
  if (!ipv6Match) return false;
  const addr = ipv6Match[1].toLowerCase();
  return (
    addr.startsWith("fc") ||
    addr.startsWith("fd") ||
    addr.startsWith("fe8") ||
    addr.startsWith("fe9") ||
    addr.startsWith("fea") ||
    addr.startsWith("feb")
  );
};

const toHostname = (hostOrUrl: string): string => {
  const normalized = hostOrUrl.trim().toLowerCase();

  try {
    // Supports values like:
    // - localhost
    // - localhost:3000
    // - [::1]:5173
    // - https://localhost:3000
    const value = normalized.includes("://")
      ? normalized
      : `http://${normalized}`;
    const hostname = new URL(value).hostname.toLowerCase();
    return hostname;
  } catch {
    return normalized;
  }
};

const isLoopbackHost = (hostOrUrl: string): boolean => {
  const hostname = toHostname(hostOrUrl);
  return LOOPBACK_HOSTNAMES.has(hostname);
};

const isDevLocal = (hostOrUrl: string): boolean => {
  if (import.meta.env.MODE !== "development") return false;
  const hostname = toHostname(hostOrUrl);
  return isLoopbackHost(hostname) || isPrivateNetworkIP(hostname);
};

const isAllowedRedirectDomain = (hostOrUrl: string): boolean => {
  const normalized = toHostname(hostOrUrl);

  if (isDevLocal(normalized)) {
    // Allow any loopback port in dev as long as loopback is allowlisted.
    return true;
  }

  const matchedDomain = config.allowedRedirectDomains.find((allowedDomain) => {
    const allowedHostname = toHostname(allowedDomain);
    return (
      normalized === allowedHostname ||
      normalized.endsWith(`.${allowedHostname}`)
    );
  });

  return Boolean(matchedDomain);
};

const getCandidateRedirectUrl = (): string => {
  const params = new URLSearchParams(window.location.search);

  const redirectUrl = params.get(REDIRECT_PARAM_KEY);
  if (!redirectUrl) {
    // return default redirect url if not present in the URL
    return config.redirectUrl;
  }

  return redirectUrl;
};

const buildSafeRedirectUrl = (): URL => {
  const candidate = getCandidateRedirectUrl();

  if (!candidate) {
    throw new Error("Redirect URL is not configured.");
  }

  let url: URL;

  try {
    url = new URL(candidate);
  } catch {
    throw new Error("Redirect URL is invalid.");
  }

  const devLocal = isDevLocal(url.hostname);

  if (devLocal) {
    if (url.protocol !== "http:" && url.protocol !== "https:") {
      throw new Error("Redirect URL must use HTTP or HTTPS.");
    }
  } else if (url.protocol !== "https:") {
    throw new Error("Redirect URL must use HTTPS.");
  }

  if (url.username || url.password) {
    throw new Error("Redirect URL must not contain credentials.");
  }

  if (!devLocal && url.port && url.port !== "443") {

    throw new Error("Redirect URL must use the default HTTPS port.");
  }

  if (!isAllowedRedirectDomain(url.hostname)) {
    throw new Error("Redirect domain is not allowed.");
  }

  return url;
};

export const loginWithGoogle = async (googleToken: string): Promise<void> => {
  const redirectUrlObj = buildSafeRedirectUrl();
  const hostnameParts = redirectUrlObj.hostname.split(".");
  const application = hostnameParts[0];

  try {
    const response = await axios.post(
      config.loginApiUrl,
      {
        type: "GOOGLE",
        google_token: googleToken,
        source: "auth_core",
      },
      {
        headers: {
          "Content-Type": "application/json",
          "Vammo-Application": application,
        },
      }
    );

    const { token } = response.data;

    if (token) {
      // Since we can't set cookies on a different domain from JavaScript,
      // we'll pass the token as a URL parameter so the backend at redirectUrl
      // can read it and set the cookie in its own domain
      redirectUrlObj.searchParams.set("token", token);

      // Redirect with token as URL parameter
      // The backend at redirectUrl should read the token and set the cookie
      window.location.href = redirectUrlObj.toString();
    } else {
      throw new Error("No token received from server");
    }
  } catch (error) {
    console.error("Login error:", error);
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      throw new Error(
        "Código 401: Conta não registrada, sem permissões ou fora do domínio @vammo.com"
      );
    }
    throw error;
  }
};